import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateChannelBinding } from '@/lib/api/channel-bindings';
import { ApiError } from '@/lib/api/client';
import { BindingForm } from '@/routes/gateway/binding-form';

export function BindingNewPage({ connectionId }: { connectionId?: string }) {
  const createBinding = useCreateChannelBinding();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">New binding</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Route a connection + strategy to a pinned agent version.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Binding</CardTitle>
          <CardDescription>
            One binding per connection + strategy (+ surface for draft-on-inbound, + Slack channel
            for the every-message strategies); the agent version is required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BindingForm
            initial={
              connectionId
                ? {
                    channelConnectionId: connectionId,
                    strategyKey: 'reply-to-dm',
                    agentId: '',
                    enabled: true,
                    surface: null,
                    externalScope: null,
                  }
                : undefined
            }
            submitting={createBinding.isPending}
            submitLabel="Create binding"
            error={error}
            onCancel={() =>
              void navigate(
                connectionId
                  ? { to: '/gateway/connections/$connectionId', params: { connectionId } }
                  : { to: '/gateway/connections' },
              )
            }
            onSubmit={(values) => {
              setError(null);
              createBinding.mutate(values, {
                onSuccess: (binding) =>
                  void navigate({
                    to: '/gateway/connections/$connectionId',
                    params: { connectionId: binding.channelConnectionId },
                  }),
                onError: (caught) =>
                  setError(
                    caught instanceof ApiError ? caught.message : 'Could not create the binding.',
                  ),
              });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
